import React, { useState } from 'react';
import styled from 'styled-components';
import VersionSelector from '../components/common/VersionSelector';
import PageContainer from '../view/page-container';
import {
  privacyPolicyVersions,
  getLatestVersion,
} from '../data/privacyPolicyVersions';

const MainContent = styled.div`
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 32px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }
`;

const SubTitle = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 40px 0 20px 0;
`;

const Article = styled.div`
  margin-bottom: 32px;
`;

const ArticleTitle = styled.h3`
  color: #000;

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 158%; /* 25.28px */
  letter-spacing: -0.4px;
`;

const ArticleBlockquote = styled.blockquote`
  margin: 8px 0;
  padding: 0;
  border: none;

  ol {
    margin: 0;
    padding-left: 24px;
  }

  li {
    list-style-type: decimal;
    margin-bottom: 4px;
  }
`;

const ArticleBlockquoteText = styled.p`
  margin: 0;
  padding-left: 24px;
`;

const ArticleText = styled.p`
  color: #000;

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 158%;
  letter-spacing: -0.4px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 40px 0;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 12px 0 20px;
`;

const PolicyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;

  th,
  td {
    border: 1px solid #ddd;
    padding: 12px;
    text-align: left;
    vertical-align: top;
    color: #000;
    font-size: 15px;
    line-height: 1.58;
    letter-spacing: -0.3px;
    word-break: keep-all;
  }

  th {
    background: #f7f7f7;
    font-weight: 600;
  }
`;

const PrivacyPolicy: React.FC = () => {
  const [selectedVersion, setSelectedVersion] = useState(
    getLatestVersion().version,
  );

  const currentVersion =
    privacyPolicyVersions.find((v) => v.version === selectedVersion) ||
    getLatestVersion();

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
  };

  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <Title>{currentVersion.title}</Title>
          <VersionSelector
            versions={privacyPolicyVersions.map((version) => ({
              version: version.version,
              effectiveDate: version.version,
              title: version.title,
              isLatest: version.isLatest,
            }))}
            onVersionChange={handleVersionChange}
          />

          <Divider />

          <Article>
            <ArticleText>
              빅브라운 주식회사(이하 “회사”)는 이용자의 개인정보를 소중하게
              생각하며, 「개인정보 보호법」 등 관련 법령을 준수합니다.
            </ArticleText>
            <ArticleText>
              회사는 개인정보의 처리 목적, 수집 항목, 보유 기간, 이용자의 권리
              및 보호 조치에 관하여 다음과 같이 규정합니다.
            </ArticleText>
          </Article>

          <SubTitle>제1조 (목적)</SubTitle>
          <Article>
            <ArticleText>
              본 방침은 회사가 제공하는 팬 경험 기반 이벤트 중개 플랫폼
              “라라팡”(이하 “서비스”)을 이용하는 회원의 개인정보를 어떠한 목적과
              방식으로 수집·이용·보관·파기하는지 명확히 함을 목적으로 합니다.
            </ArticleText>
            <ArticleText>
              회사는 이용자의 개인정보를 안전하게 관리하고, 이용자의 권리를
              보호하기 위해 본 방침을 수립·공개합니다.
            </ArticleText>
          </Article>

          <SubTitle>제2조 (개인정보 수집의 원칙)</SubTitle>
          <Article>
            <ArticleText>
              회사는 서비스 제공에 필요한 최소한의 개인정보만을 수집·이용합니다.
            </ArticleText>
            <ArticleText>
              개인정보는 「개인정보 보호법」 제15조 각 호에 근거하여 수집하며,
              이용자의 사전 동의 또는 법령상 근거 없이 제3자에게 제공하지
              않습니다.
            </ArticleText>
            <ArticleText>
              회사는 이용 목적이 달성된 개인정보를 지체 없이 파기하며, 관련
              법령에서 정한 기간 동안만 보유·이용합니다.
            </ArticleText>
          </Article>

          <SubTitle>제3조 (서비스 열람 및 연령·가입 정책)</SubTitle>
          <Article>
            <ArticleText>
              회사는 서비스 내 공개된 이벤트 목록, 소개 페이지 등은 모든 이용자가
              열람할 수 있도록 운영할 수 있습니다.
            </ArticleText>
            <ArticleText>
              다만, 회원 가입은 만 14세 이상부터 허용하며, 만 14세 미만으로
              확인되는 경우 가입 또는 이용이 제한되고 개인정보는 지체 없이
              파기됩니다.
            </ArticleText>
            <ArticleText>
              유료 결제 및 유료 이벤트 참여는 성인(대한민국 기준 만 19세
              이상)이며, 본인 명의의 결제수단을 보유한 회원에 한하여 가능합니다.
            </ArticleText>
            <ArticleText>
              일부 오프라인 참여형 이벤트는 안전·책임 등의 사유로 ‘성인만 참여
              가능’, ‘보호자 동반 필요’, ‘청소년 참여 가능’ 등의 조건을 둘 수
              있으며, 회사는 결제 또는 신청 단계에서 이를 고지합니다.
            </ArticleText>
          </Article>

          <SubTitle>제4조 (개인정보 수집 항목 및 처리 목적)</SubTitle>
          <Article>
            <ArticleText>
              회사는 서비스 제공 및 운영, 회원 관리, 결제·정산, 고객 문의 대응 등
              필요한 범위 내에서 개인정보를 처리합니다.
            </ArticleText>
            <ArticleText>
              개인정보의 수집·이용 목적 및 항목은 다음 각 호와 같습니다.
            </ArticleText>
            <ArticleBlockquote>
              <ol>
                <li>
                  <ArticleTitle>회원가입 및 서비스 운영</ArticleTitle>
                  <ArticleText>
                    목적: 가입 의사 확인, 본인 식별·인증, 회원 관리, 공지 및 알림
                    발송
                  </ArticleText>
                  <ArticleText>항목:</ArticleText>
                  <ArticleBlockquoteText>
                    <ArticleText>· 이메일 주소, 비밀번호(이메일 가입 시)</ArticleText>
                    <ArticleText>· 이름 또는 닉네임, 생년월일, 휴대폰번호</ArticleText>
                    <ArticleText>· 성별(선택)</ArticleText>
                    <ArticleText>· 본인인증 식별값(DI/CI 등)</ArticleText>
                    <ArticleText>· 계정 상태, 공지·안내 기록</ArticleText>
                    <ArticleText>
                      · 이용자가 작성한 게시물, 이벤트 소개 내용, 후기, 댓글 등
                    </ArticleText>
                    <ArticleText>
                      · 소셜 로그인 시 제공되는 정보: 이름(또는 닉네임), 이메일,
                      프로필 이미지 등
                    </ArticleText>
                  </ArticleBlockquoteText>
                  <ArticleText>
                    보유기간: 회원 탈퇴 시까지 (단, 법령상 보존 의무가 있는 경우
                    예외)
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>소셜 로그인 연동</ArticleTitle>
                  <ArticleText>목적: 로그인 간소화, 계정 연동 관리</ArticleText>
                  <ArticleText>
                    항목: 이름(또는 닉네임), 이메일, 프로필 이미지
                  </ArticleText>
                  <ArticleText>
                    보유기간: 회원 탈퇴 또는 소셜 계정 연동 해제 시까지
                  </ArticleText>
                  <ArticleText>
                    비고: 동일 이메일 계정의 자동 연동 시, 이용자는 명시적으로
                    ‘연동하기’에 동의하며, 연동 해제는 [내 계정 설정] {'>'} [연동
                    관리]에서 가능합니다.
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>결제 및 정산</ArticleTitle>
                  <ArticleText>
                    목적: 유료 이벤트 결제, 환불·취소 처리, 호스트 또는 제휴 법인을
                    통한 정산 처리, 세무 처리 및 지급명세서 작성 등
                  </ArticleText>
                  <ArticleText>항목:</ArticleText>
                  <ArticleBlockquoteText>
                    <ArticleText>
                      · 이름, 생년월일, 휴대폰번호, 이메일주소
                    </ArticleText>
                    <ArticleText>
                      · 결제정보(결제수단 토큰, 승인번호 등 PG사 제공값)
                    </ArticleText>
                    <ArticleText>
                      · 주문·참여 내역(이벤트명, 수량, 금액, 일시 등)
                    </ArticleText>
                    <ArticleText>
                      · 정산계좌 정보(은행명, 계좌번호, 예금주명)
                    </ArticleText>
                    <ArticleText>
                      · 세무 처리용 주민등록번호 또는 사업자등록번호
                    </ArticleText>
                  </ArticleBlockquoteText>
                  <ArticleText>
                    보유기간: 5년 (전자상거래법 제6조)
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>고객 문의 및 분쟁 처리</ArticleTitle>
                  <ArticleText>
                    목적: 문의·불편사항 처리, 환불/취소/분쟁 대응
                  </ArticleText>
                  <ArticleText>
                    항목: 이메일 또는 아이디, 이름, 연락처, 문의 내용, 결제 내역,
                    정산 기록
                  </ArticleText>
                  <ArticleText>
                    보유기간: 3년 (전자상거래법 제6조)
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>부정이용 방지</ArticleTitle>
                  <ArticleText>
                    목적: 허위 이벤트 등록, 결제 사기, 반복 환불 악용 등 부정행위
                    탐지 및 재발 방지, 이용자 보호
                  </ArticleText>
                  <ArticleText>
                    항목: 접속IP, 접속일시, 기기/브라우저 정보, 결제·환불 시도
                    이력, 부정이용 탐지·차단 기록(필요 최소 항목) 및 본인확인
                    절차가 이루어진 경우에 한하여 본인확인 식별값(DI/CI)
                  </ArticleText>
                  <ArticleText>
                    보유기간: 회원 탈퇴 후 3개월(부정이용 재발 방지 및 분쟁 대응을
                    위한 최소 기간) 보관 후 파기. 단, 해당 정보는 분리 보관하며
                    목적 외 이용을 금지하고 접근권한을 최소화합니다.
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>법령상 보존</ArticleTitle>
                  <ArticleText>목적: 전자상거래 관련 법령 등 의무 이행</ArticleText>
                  <ArticleText>항목 및 기간:</ArticleText>
                  <ArticleBlockquoteText>
                    <ArticleText>
                      · 결제/청약철회/소비자 불만기록: 3~5년
                    </ArticleText>
                    <ArticleText>
                      · 서비스 접속 기록(IP, 일시 등): 3개월 (통신비밀보호법)
                    </ArticleText>
                  </ArticleBlockquoteText>
                </li>
                <li>
                  <ArticleTitle>브랜드 계정 전환 심사</ArticleTitle>
                  <ArticleText>
                    목적: 인플루언서 계정을 브랜드 계정으로 전환하고, 신청자가 해당
                    브랜드 채널의 정당한 관리자임을 확인하기 위함
                  </ArticleText>
                  <ArticleText>항목:</ArticleText>
                  <ArticleBlockquoteText>
                    <ArticleText>
                      · 서비스 내에 이용자가 입력한 브랜드 채널명, SNS 채널
                      계정명/ID 및 URL
                    </ArticleText>
                    <ArticleText>
                      · 라라팡 공식 인스타그램 계정(lalafan.official)으로 발송된 DM
                      내 정보(브랜드 계정명, 연락자 정보 등)
                    </ArticleText>
                    <ArticleText>
                      · 상기 정보와 불일치하는 경우, 이용자가 브랜드 계정의
                      관리자임을 확인하기 위하여 제출하는 증빙자료(예:
                      사업자등록증, 재직증명서, 명함, 브랜드 도메인 이메일 회신 등)
                    </ArticleText>
                  </ArticleBlockquoteText>
                  <ArticleText>처리 및 보관:</ArticleText>
                  <ArticleBlockquoteText>
                    <ArticleText>
                      · 브랜드 계정 전환 신청일로부터 5영업일 이내에 위 DM 또는
                      증빙자료가 회사에 도달하지 않거나, 제출된 정보만으로 관리자
                      권한이 확인되지 않는 경우 회사는 브랜드 계정 전환 신청을
                      반려할 수 있으며, 이 경우 해당 계정은 다시 일반 인플루언서
                      계정 상태로 유지·전환됩니다.
                    </ArticleText>
                    <ArticleText>
                      · 회사는 제출된 정보와 증빙을 확인한 후 브랜드 계정 전환을
                      승인하거나 반려하며, 이용자는 반려된 경우에도 언제든지 다시
                      브랜드 계정 전환을 신청할 수 있습니다.
                    </ArticleText>
                    <ArticleText>
                      · 브랜드 계정 전환 심사와 관련하여 수집·생성된 기록은 심사
                      완료일(승인 또는 반려)로부터 3년간 보관한 뒤, 관련 법령에
                      다른 규정이 없는 한 지체 없이 파기합니다.
                    </ArticleText>
                  </ArticleBlockquoteText>
                </li>
              </ol>
            </ArticleBlockquote>
          </Article>

          <SubTitle>제5조 (동의에 따른 추가 수집 항목)</SubTitle>
          <Article>
            <ArticleText>
              회사는 이용자의 사전 동의하에 다음 항목을 추가 수집·이용할 수
              있습니다.
            </ArticleText>
            <ArticleText>
              동의 거부 시 핵심 서비스 이용에는 제한이 없으나, 일부 부가 기능이
              제한될 수 있습니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>· 개인화 추천 및 알림 서비스</ArticleText>
              <ArticleText>
                목적: 관심사 기반 추천, 맞춤형 알림, 신규 기능 안내
              </ArticleText>
              <ArticleText>
                항목: 닉네임, 관심 카테고리, 이용 이력, 디바이스ID, 쿠키,
                광고식별자
              </ArticleText>
              <ArticleText>
                보유기간: 동의 철회 또는 회원 탈퇴 후 6개월
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleBlockquoteText>
              <ArticleText>· 마케팅·프로모션 안내</ArticleText>
              <ArticleText>
                목적: 서비스 소식, 이벤트, 쿠폰 발송, 프로모션 알림
              </ArticleText>
              <ArticleText>
                항목: 이름(또는 닉네임), 연락처, 이메일, 광고식별자, 참여/구매
                이력
              </ArticleText>
              <ArticleText>
                보유기간: 동의 철회 또는 회원 탈퇴 후 6개월
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleBlockquoteText>
              <ArticleText>· 이벤트 진행 및 경품 지급</ArticleText>
              <ArticleText>목적: 경품 발송, 현장 안내, 참여 확인</ArticleText>
              <ArticleText>
                항목: 이름(또는 닉네임), 휴대폰번호, 이메일, 주소(필요 시)
              </ArticleText>
              <ArticleText>보유기간: 이벤트 종료 후 6개월</ArticleText>
            </ArticleBlockquoteText>
          </Article>

          <SubTitle>제6조 (본인 결제수단 원칙)</SubTitle>
          <Article>
            <ArticleText>
              모든 유료 결제는 회원 본인 명의의 결제수단으로만 가능합니다.
            </ArticleText>
            <ArticleText>
              타인 명의의 결제수단을 무단으로 사용하는 경우 회사는 결제 취소, 계정
              제한, 법적 조치를 취할 수 있습니다.
            </ArticleText>
            <ArticleText>
              회사는 결제 사기 방지 등을 위해 필요 시 신분증 사본, 결제수단 소유
              증명 등 추가 확인 자료를 요청할 수 있습니다.
            </ArticleText>
          </Article>

          <SubTitle>제7조 (개인정보 수집 방법)</SubTitle>
          <Article>
            <ArticleText>회사는 다음의 방법으로 개인정보를 수집합니다.</ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 회원가입, 이벤트 신청, 결제 과정에서 이용자가 직접 입력하는 정보
              </ArticleText>
              <ArticleText>
                · 서비스 이용 중 자동으로 생성·수집되는 정보(IP, 접속로그,
                기기정보 등)
              </ArticleText>
              <ArticleText>
                · 고객센터 문의, 분쟁 처리 과정에서 추가로 제공되는 정보
              </ArticleText>
              <ArticleText>
                · 제휴 파트너를 통한 제공(사전 동의 기반, 제3자 제공에 해당)
              </ArticleText>
              <ArticleText>
                · 브랜드 계정 전환을 위하여 이용자가 라라팡 공식 인스타그램
                계정(lalafan.official)으로 직접 발송하는 DM 및 해당 DM을 통해
                전달되는 정보
              </ArticleText>
            </ArticleBlockquoteText>
          </Article>

          <SubTitle>제8조 (개인정보의 제3자 제공)</SubTitle>
          <Article>
            <ArticleText>
              회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다.
            </ArticleText>
            <ArticleText>
              다만, 다음 각 호의 경우 예외적으로 제공될 수 있습니다.
            </ArticleText>
            <ArticleBlockquote>
              <ol>
                <li>
                  <ArticleTitle>법령상 요청</ArticleTitle>
                  <ArticleText>제공 대상: 수사기관, 행정기관 등</ArticleText>
                  <ArticleText>
                    제공 목적: 수사·조사·재난대응 등 법령상 근거 있는 경우
                  </ArticleText>
                  <ArticleText>
                    제공 항목: 요청 범위 내 최소 정보
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>이벤트 진행을 위한 제공(호스트 제공)</ArticleTitle>
                  <ArticleText>
                    제공 대상: 해당 이벤트의 주최 인플루언서(호스트)
                  </ArticleText>
                  <ArticleText>
                    제공 목적: 참여자 관리, 일정·안전 공지, 현장 운영
                  </ArticleText>
                  <ArticleText>
                    제공 항목: 이름(또는 닉네임), 연락처, 신청 인원 등
                  </ArticleText>
                  <ArticleText>
                    보유기간: 이벤트 종료 및 정산 완료 후 최대 6개월
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>이벤트 운영을 위한 제공(참여자 제공)</ArticleTitle>
                  <ArticleText>
                    제공 대상: 해당 이벤트의 확정 참여자
                  </ArticleText>
                  <ArticleText>
                    제공 목적: 일정·장소 안내, 현장 담당자 연락 등
                  </ArticleText>
                  <ArticleText>
                    제공 항목: 호스트 표시명(운영명), 연락 수단, 집결지 정보
                  </ArticleText>
                  <ArticleText>
                    보유기간: 이벤트 종료 및 정산 완료 후 최대 6개월
                  </ArticleText>
                </li>
                <li>
                  <ArticleTitle>비사업자 인플루언서 정산을 위한 제공</ArticleTitle>
                  <ArticleText>
                    제공 대상: 제휴 법인 (주식회사 아보카도자이언트)
                  </ArticleText>
                  <ArticleText>
                    제공 목적: 비사업자 인플루언서와 체결된 용역계약의 이행, 팬밋
                    운영 관리, 정산 지급, 세무 처리
                  </ArticleText>
                  <ArticleText>
                    제공 항목: 이름, 연락처, 이메일 주소, 정산 대상 팬밋
                    정보(이벤트명, 일정, 금액), 정산 계좌 정보, 세무 처리에 필요한
                    식별 정보 (주민등록번호 등 고유식별정보)
                  </ArticleText>
                  <ArticleText>
                    단, 관련 법령에 따른 세무 처리 또는 본인 확인이 필요한 경우에
                    한하여 최소한의 범위로 수집하며, 주민등록증 사본은 법령상
                    요구되거나 불가피한 경우에 한해 제한적으로 수집합니다.
                  </ArticleText>
                  <ArticleText>
                    보유기간: 정산 목적 달성 및 관련 법령상 보존 기간 종료 시까지
                  </ArticleText>
                  <ArticleText>
                    제휴 법인은 관련 법령에 따른 보유기간이 경과하면 지체 없이
                    파기합니다.
                  </ArticleText>
                  <ArticleText>
                    비사업자 인플루언서의 경우, 서비스 이용약관에 따라 제휴 법인과
                    용역계약이 체결되며, 해당 계약의 이행을 위하여 필요한 범위
                    내에서 개인정보가 제3자에게 제공될 수 있습니다.
                  </ArticleText>
                </li>
              </ol>
            </ArticleBlockquote>
            <ArticleText>
              회사는 위 목적 외의 개인정보 제공을 금지하며, 목적 달성 후 즉시
              파기하도록 요청합니다.
            </ArticleText>
          </Article>

          <SubTitle>제9조 (개인정보 처리의 위탁 및 국외 이전)</SubTitle>
          <Article>
            <ArticleText>
              1. 회사는 원활한 서비스 제공을 위해 일부 업무를 외부 전문기관에
              위탁할 수 있습니다.
            </ArticleText>
            <ArticleText>
              2. 주요 위탁 대상 및 내용은 다음과 같습니다.
            </ArticleText>
            <TableWrapper>
              <PolicyTable>
                <thead>
                  <tr>
                    <th>수탁자</th>
                    <th>위탁 업무</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>토스페이먼츠 주식회사</td>
                    <td>
                      결제 승인, 결제 취소, 환불 처리 등 전자결제 서비스 운영
                    </td>
                  </tr>
                  <tr>
                    <td>NHN Cloud 주식회사</td>
                    <td>SMS 문자 발송 서비스 운영(인증번호 발송 포함)</td>
                  </tr>
                  <tr>
                    <td>Google Firebase (Google LLC)</td>
                    <td>푸시 알림 발송 및 관련 메시지 서비스 운영</td>
                  </tr>
                  <tr>
                    <td>Amazon Web Services, Inc. (AWS)</td>
                    <td>
                      서버 인프라 운영, 데이터베이스(DB) 관리, 이메일 발송 시스템
                      운영, 클라우드 기반 서비스 운영
                    </td>
                  </tr>
                </tbody>
              </PolicyTable>
            </TableWrapper>
            <ArticleText>
              3. 위탁업체가 변경될 경우 회사는 본 방침을 통하여 지체 없이
              공지합니다.
            </ArticleText>
            <ArticleText>
              4. 회사는 다음과 같이 국외에 개인정보를 이전할 수 있습니다.
            </ArticleText>
            <TableWrapper>
              <PolicyTable>
                <thead>
                  <tr>
                    <th>수탁자</th>
                    <th>이전국가</th>
                    <th>이전 항목</th>
                    <th>이전 시점 및 방법</th>
                    <th>보유 및 이용 기간</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google Firebase (Google LLC)</td>
                    <td>미국</td>
                    <td>
                      디바이스 식별자(ADID/IDFA), 푸시 토큰, 앱 이용 로그, 오류
                      로그, IP 주소 등
                    </td>
                    <td>서비스 이용 과정에서 네트워크를 통해 전송</td>
                    <td>서비스 탈퇴 또는 목적 달성 시까지</td>
                  </tr>
                  <tr>
                    <td>Amazon Web Services, Inc. (AWS)</td>
                    <td>미국(본사) / 해외 리전 가능</td>
                    <td>서버 운영 로그, 이메일 발송 기록, 백업 데이터 등</td>
                    <td>
                      클라우드 서비스 운영 과정에서 전송 또는 기술지원 과정에서
                      이전될 수 있음
                    </td>
                    <td>법령상 보존 기간 또는 서비스 제공 기간 동안</td>
                  </tr>
                </tbody>
              </PolicyTable>
            </TableWrapper>
            <ArticleText>
              5. 회사는 국외 이전되는 개인정보의 보호를 위하여 암호화, 접근권한
              최소화 등 필요한 보호조치를 이행합니다.
            </ArticleText>
            <ArticleText>
              6. 비사업자 인플루언서의 용역계약 및 정산을 위하여 제휴 법인에
              제공되는 개인정보는 「개인정보 보호법」 제17조에 따른 제3자 제공에
              해당하며, 본 조에 따른 개인정보 처리 위탁에는 해당하지 않습니다.
            </ArticleText>
          </Article>

          <SubTitle>제10조 (개인정보의 보유 및 파기)</SubTitle>
          <Article>
            <ArticleText>
              회사는 개인정보 보유기간이 경과하거나 처리 목적이 달성된 경우 지체
              없이 파기합니다.
            </ArticleText>
            <ArticleText>
              법령상 보존 의무가 있는 경우에는 별도 DB 또는 분리된 저장공간에
              안전하게 보관합니다.
            </ArticleText>
            <ArticleText>파기 방법은 다음과 같습니다.</ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 전자파일: 복구 불가능한 기술적 방법으로 삭제
              </ArticleText>
              <ArticleText>· 종이문서: 분쇄 또는 소각 처리</ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>회원 탈퇴 시 개인정보 파기</ArticleText>
            <ArticleText>
              회원이 탈퇴를 신청하거나 서비스 이용계약이 종료되는 경우, 회사는
              다음 각 호에 해당하는 개인정보를 지체 없이 파기합니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 회원 계정 정보(이메일 주소, 비밀번호, 휴대폰번호, 닉네임, 프로필
                정보 등)
              </ArticleText>
              <ArticleText>· 소셜 로그인 연동 정보</ArticleText>
              <ArticleText>
                · 서비스 이용 중 생성된 일반 로그 및 알림 기록 중 법령상 보존
                의무가 없는 정보
              </ArticleText>
              <ArticleText>
                · 브랜드 계정 전환 신청 중 제출된 자료(심사 기록은 제4조
                제7항에 따라 별도 보관되는 기간을 따름)
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              다만, 다음 각 호의 정보는 관련 법령에 따라 정해진 기간 동안
              보존하며, 이 기간 동안 다른 목적을 위하여 이용하지 않습니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>· 대금결제 및 재화 등의 공급에 관한 기록: 5년</ArticleText>
              <ArticleText>
                · 소비자 불만 또는 분쟁처리에 관한 기록: 3년
              </ArticleText>
              <ArticleText>· 계약 또는 청약철회 등에 관한 기록: 5년</ArticleText>
              <ArticleText>
                · 접속기록(IP, 접속 일시 등): 3개월
              </ArticleText>
              <ArticleText>
                · 부정이용 방지를 위한 기록(DI/CI 등): 회원 탈퇴 후 3개월
              </ArticleText>
              <ArticleText>
                · 브랜드 계정 전환 심사에 따른 기록: 심사 완료일로부터 3년
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              회원이 작성한 게시물·후기·댓글 등은 삭제 요청이 없는 경우 서비스 내
              게시물로 계속 보존될 수 있으며, 게시물 삭제를 원하는 경우 탈퇴 전에
              직접 삭제하거나 고객센터를 통해 별도 요청할 수 있습니다.
            </ArticleText>
            <ArticleText>
              회사는 파기 대상 개인정보를 재생이 불가능한 방법으로 영구 삭제합니다.
            </ArticleText>
          </Article>

          <SubTitle>제11조 (개인정보의 안전성 확보조치)</SubTitle>
          <Article>
            <ArticleText>
              회사는 개인정보의 안전한 처리를 위해 다음의 조치를 취합니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 관리적 조치: 내부관리계획 수립, 임직원 교육, 접근 권한 관리
              </ArticleText>
              <ArticleText>
                · 기술적 조치: 접근 통제, 암호화 저장, 보안프로그램 설치, 로그
                위·변조 방지
              </ArticleText>
              <ArticleText>
                · 물리적 조치: 전산실·보관실 잠금 관리, 재해·재난 대비 조치
              </ArticleText>
              <ArticleText>
                · 이상 징후 모니터링: 결제 사기, 계정 도용, 허위 이벤트 등록 등
                부정 이용 방지 로직 운영
              </ArticleText>
            </ArticleBlockquoteText>
          </Article>

          <SubTitle>제12조 (쿠키 및 자동수집기술의 이용)</SubTitle>
          <Article>
            <ArticleText>
              회사는 이용자의 편의를 위해 쿠키(cookie), 광고식별자(ADID/IDFA),
              디바이스ID 등을 활용할 수 있습니다.
            </ArticleText>
            <ArticleText>
              이용자는 브라우저 및 단말 설정을 통해 쿠키 저장을 거부하거나 삭제할
              수 있습니다.
            </ArticleText>
            <ArticleText>쿠키 차단 방법 예시:</ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 크롬: 설정 {'>'} 개인정보 및 보안 {'>'} 쿠키 및 기타 사이트
                데이터
              </ArticleText>
              <ArticleText>
                · 모바일: OS 설정 {'>'} 광고 맞춤설정 제한 또는 추적 거부
              </ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              쿠키를 거부할 경우 일부 기능 이용에 제한이 있을 수 있습니다.
            </ArticleText>
            <ArticleText>
              회사는 광고 효율 분석 및 서비스 개선을 위하여 익명·통계 형태의
              행태정보를 처리할 수 있으며, 민감정보는 수집하지 않습니다.
            </ArticleText>
          </Article>

          <SubTitle>제13조 (이용자 및 법정대리인의 권리)</SubTitle>
          <Article>
            <ArticleText>
              이용자는 언제든지 개인정보의 열람, 정정, 삭제, 처리정지, 동의 철회
              등을 요청할 수 있습니다.
            </ArticleText>
            <ArticleText>
              회사는 요청을 받은 날로부터 10일 이내에 회신하며, 부득이한 사유가
              있을 경우 그 사유와 일정을 안내합니다.
            </ArticleText>
            <ArticleText>
              회원은 [프로필/계정관리] 메뉴에서 직접 정보를 수정하거나,
              고객센터를 통해 요청할 수 있습니다.
            </ArticleText>
            <ArticleText>
              법정대리인 또는 위임을 받은 자는 위임장을 제출하여 대리로 권리를
              행사할 수 있습니다.
            </ArticleText>
            <ArticleText>
              만 14세 미만으로 확인되는 경우 회사는 즉시 계정을 제한·삭제하며,
              법정대리인은 해당 아동의 개인정보 열람·정정을 요구할 수 있습니다.
            </ArticleText>
          </Article>

          <SubTitle>제14조 (외부 서비스에 대한 책임)</SubTitle>
          <Article>
            <ArticleText>
              서비스에는 인플루언서 또는 제휴 파트너가 제공하는 외부
              웹사이트·콘텐츠로 연결되는 링크가 포함될 수 있습니다.
            </ArticleText>
            <ArticleText>
              회사는 외부 서비스의 개인정보 처리에 대해 통제할 수 없으며, 본
              방침이 적용되지 않습니다.
            </ArticleText>
            <ArticleText>
              외부 사이트 이용 시 해당 서비스의 개인정보처리방침을 확인해야
              합니다.
            </ArticleText>
          </Article>

          <SubTitle>제15조 (개인정보 보호책임자)</SubTitle>
          <Article>
            <ArticleText>
              회사는 개인정보보호 관련 업무를 총괄하는 개인정보보호책임자를
              지정합니다.
            </ArticleText>
            <ArticleText>
              개인정보보호책임자 및 연락처는 다음과 같습니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>· 성명: 홍준호</ArticleText>
              <ArticleText>· 소속: 플랫폼비즈니스사업부</ArticleText>
              <ArticleText>· 직위: 차장</ArticleText>
              <ArticleText>· 이메일: hello@lalafan.io</ArticleText>
            </ArticleBlockquoteText>
            <ArticleText>
              개인정보 관련 문의·불만·피해구제 요청은 위 담당자에게 연락할 수
              있으며, 회사는 신속히 답변합니다.
            </ArticleText>
          </Article>

          <SubTitle>제16조 (권익침해 구제방법)</SubTitle>
          <Article>
            <ArticleText>
              이용자는 아래 기관을 통해 개인정보 침해에 대한 상담 및 분쟁조정을
              요청할 수 있습니다.
            </ArticleText>
            <ArticleBlockquoteText>
              <ArticleText>
                · 개인정보분쟁조정위원회: (국번없이) 1833-6972
              </ArticleText>
              <ArticleText>· 개인정보침해신고센터: (국번없이) 118</ArticleText>
              <ArticleText>· 대검찰청 사이버수사과: 1301</ArticleText>
              <ArticleText>· 경찰청 사이버수사국: 182</ArticleText>
            </ArticleBlockquoteText>
          </Article>

          <SubTitle>제17조 (방침의 변경)</SubTitle>
          <Article>
            <ArticleText>
              본 개인정보 처리방침은 2025년 11월 1일부터 시행됩니다.
            </ArticleText>
            <ArticleText>
              회사는 관련 법령, 서비스 내용 변경 시 최소 7일 전(중대한 변경은
              30일 전) 공지사항 등을 통해 사전 안내합니다.
            </ArticleText>
          </Article>

          <SubTitle>[부칙]</SubTitle>
          <Article>
            <ArticleText>최초 제정일: 2025년 11월 1일</ArticleText>
            <ArticleText>시행일: 2025년 11월 1일</ArticleText>
          </Article>
          <Article>
            <ArticleTitle>회사정보</ArticleTitle>
            <ArticleText>회사명: 주식회사 빅브라운</ArticleText>
            <ArticleText>대표: 김용환</ArticleText>
            <ArticleText>사업자등록번호: 606-87-02388</ArticleText>
            <ArticleText>
              주소: 서울특별시 강남구 테헤란로 437, 10F (삼성동, 삼영빌딩)
            </ArticleText>
            <ArticleText>대표 이메일: hello@lalafan.io</ArticleText>
          </Article>
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default PrivacyPolicy;
